import type {
  InstructionBlock,
  PresentWhen,
  QuestionnaireOut,
  StudyProtocolConfig,
  StudyQuestionnaire,
} from "../../api/types";

// Mirrors the server-side DEFAULT_PROTOCOL (the public study's behavior) so the
// portal renders sensibly before the protocol fetch resolves.
export const DEFAULT_PROTOCOL_CONFIG: StudyProtocolConfig = {
  photo: { methods: ["webcam", "file"], min_webcam_photos: 3, max_photos: 10, weekly_photo_limit: 70 },
  results_visible: true,
  instructions: [],
};

// The webcam capture-count phrase: "all 11" when the minimum equals the
// maximum, else the "10–11" range. Shared by DailyFlow and PhotosTab so the
// two sentences cannot drift.
export function webcamCountPhrase(photo: StudyProtocolConfig["photo"]): string {
  const { min_webcam_photos: min, max_photos: max } = photo;
  return min === max ? `all ${max}` : `${min}-${max}`;
}

// Conditional question visibility against the current answers.
export function evaluatePresentWhen(
  pw: PresentWhen | null | undefined,
  answers: Record<string, string>,
): boolean {
  if (!pw) return true;
  const answer = answers[pw.field] ?? "";
  if (pw.op === "eq") return answer === pw.value;
  if (pw.op === "ne") return answer !== pw.value;
  return Array.isArray(pw.value) && pw.value.includes(answer);
}

// Which study questionnaires are due right now. one_time → until submitted;
// daily → once per (local) calendar day, matching the built-in daily's
// client-side convention; day lists → on matching study days, once that day.
export function dueStudyQuestionnaires(
  specs: StudyQuestionnaire[],
  responses: QuestionnaireOut[],
  studyDay: number | null,
): StudyQuestionnaire[] {
  const today = new Date().toDateString();
  const submittedEver = new Set(
    responses.filter((r) => r.questionnaire_type === "study").map((r) => r.questionnaire_key),
  );
  const submittedToday = new Set(
    responses
      .filter(
        (r) =>
          r.questionnaire_type === "study" && new Date(r.submitted_at).toDateString() === today,
      )
      .map((r) => r.questionnaire_key),
  );
  return specs.filter((q) => {
    if (q.schedule === "one_time") return !submittedEver.has(q.key);
    if (q.schedule === "daily") return !submittedToday.has(q.key);
    return studyDay !== null && q.schedule.includes(studyDay) && !submittedToday.has(q.key);
  });
}

// The instruction block governing a study day: the highest from_day ≤ day wins.
export function pickInstruction(
  blocks: InstructionBlock[],
  studyDay: number | null,
): InstructionBlock | null {
  const day = studyDay ?? 0;
  const eligible = blocks.filter((b) => b.from_day <= day);
  if (eligible.length === 0) return null;
  return eligible.reduce((best, b) => (b.from_day > best.from_day ? b : best));
}
